// #genai Phase 2 saved-payment-methods list — pixel-matched to Figma 269:20146 / 269:20163 / 269:20180.
// Rendered on /billing-information when the ENABLE_MULTI_PAYMENT_METHODS flag is ON and the user has
// one or more saved methods. "Add Payment Method" opens the same payment-type menu as the empty
// state (shared AddPaymentMethodButton), routing to the dedicated add-ACH / add-card pages.
//
// Data + mutations are wired to the ecomm-orchestration BFF (see MYEV_UI_INTEGRATION_GUIDE.md):
//   - list:        GET  /v1/payment-methods            (loadPaymentMethods thunk, dispatched by parent)
//   - set default: PUT  /v1/payment-methods/:type/:id/default  (optimistic; rolls back on failure)
//   - remove:      PUT  /v1/payment-methods/:type/:id/remove   (optimistic; 409 => "pick new default")
import React, { useState } from 'lib/react';
import { useDispatch, useSelector } from 'lib/redux';
import { Trans, useTranslation } from 'lib/translation';
import { EVGrid, EVTypography, EVCircularProgress } from 'lib/components';
import Banner from 'commonComponents/banner';
import { CUSTOMER_SERVICE_EMAIL, ERROR, SERVER_ERROR_RESPONSES } from 'constants/AppConstants';
import {
  PAYMENT_METHOD_OPTION_IDS,
  PAYMENT_METHODS_ACTION_ERROR_KIND,
} from 'commonComponents/paymentInformation/PaymentInformationConstants';
import {
  selectPaymentMethodList,
  selectPaymentMethodsLoading,
  selectPaymentMethodsLoaded,
  selectPaymentMethodsActionError,
  selectPendingPaymentMethodActionKey,
} from 'utils/appSelectors.utils';
import AddPaymentMethodButton from '../components/AddPaymentMethodButton';
import SavedPaymentMethodRow from './SavedPaymentMethodRow.component';
import RemovePaymentMethodDialog from './RemovePaymentMethodDialog/RemovePaymentMethodDialog.component';
import SavedPaymentMethodsStyles from './SavedPaymentMethodsStyles';
import { mapApiPaymentMethodToViewModel, getAchAccountTypeLabelKey } from './paymentMethods.viewModel';
import { paymentMethodKey } from '../BillingInformation.reducer';
import {
  setPaymentMethodDefault,
  removePaymentMethod,
  resetPaymentMethodsActionError,
} from '../BillingInformation.actions';

const CARD_BRAND_NAMES = {
  visa: 'Visa',
  mastercard: 'Mastercard',
  amex: 'Amex',
  discover: 'Discover',
};

function SavedPaymentMethods() {
  const { t } = useTranslation();
  const classes = SavedPaymentMethodsStyles();
  const dispatch = useDispatch();

  const apiMethods = useSelector(selectPaymentMethodList);
  const isLoading = useSelector(selectPaymentMethodsLoading);
  const isLoaded = useSelector(selectPaymentMethodsLoaded);
  const actionError = useSelector(selectPaymentMethodsActionError);
  const pendingActionKey = useSelector(selectPendingPaymentMethodActionKey);

  const [removeTarget, setRemoveTarget] = useState(null);
  // #genai Bumped on Make Default so every row collapses (UX: no leftover expanded detail panels).
  const [collapseNonce, setCollapseNonce] = useState(0);

  const methods = apiMethods.map(mapApiPaymentMethodToViewModel);

  // #genai Set-default (optimistic in the reducer; saga confirms/rolls back). Collapse all rows
  // immediately so expanded detail panels don't linger after the DEFAULT badge moves.
  const handleMakeDefault = (method) => {
    setCollapseNonce((n) => n + 1);
    dispatch(setPaymentMethodDefault({ type: method.apiType, id: method.id }));
  };

  const handleRemoveRequest = (method) => setRemoveTarget(method);
  const handleRemoveCancel = () => setRemoveTarget(null);

  // #genai Remove (optimistic in the reducer; a 409 CannotRemoveDefault rolls back and surfaces the
  // blocked banner below prompting the user to pick a new default first).
  const handleRemoveConfirm = () => {
    if (removeTarget) {
      dispatch(removePaymentMethod({ type: removeTarget.apiType, id: removeTarget.id }));
    }
    setRemoveTarget(null);
  };

  const getMethodLabel = (method) => {
    if (!method) return '';
    if (method.type === PAYMENT_METHOD_OPTION_IDS.Ach) {
      return t(getAchAccountTypeLabelKey(method.accountType));
    }
    return CARD_BRAND_NAMES[(method.brand || '').toLowerCase()] || t('billing_information__pm_card');
  };

  // #genai HTTP 500 remove failures use Figma copy (lastFour title + support mailto). Other remove
  // failures keep the existing API-text / generic fallback path unchanged.
  const isRemove500 = actionError?.kind === PAYMENT_METHODS_ACTION_ERROR_KIND.Remove
    && actionError.httpStatus === SERVER_ERROR_RESPONSES.INTERNAL_SERVER_ERROR;
  const removeFailedLastFour = isRemove500
    ? (methods.find((m) => (
      m.apiType === actionError.method?.type && m.id === actionError.method?.id
    ))?.lastFour || '')
    : '';

  // #genai Initial load spinner (only before the first successful/erroring fetch resolves).
  if (isLoading && !isLoaded) {
    return (
      <EVGrid className={classes.pageWrap}>
        <div className={classes.loadingWrap}>
          <EVCircularProgress color="inherit" size={32} />
        </div>
      </EVGrid>
    );
  }

  return (
    <EVGrid className={classes.pageWrap}>
      <EVTypography className={classes.subtitle}>
        {t('billing_information__manage_payment_methods')}
      </EVTypography>

      {/* #genai 409 CannotRemoveDefault — block remove, prompt to pick a new default (guide §4.4). */}
      {actionError?.kind === PAYMENT_METHODS_ACTION_ERROR_KIND.Blocked && (
        <Banner
          severity={ERROR}
          title={t('billing_information__pm_cannot_remove_default_title')}
          message={t('billing_information__pm_cannot_remove_default_message')}
          handleAlertClose={() => dispatch(resetPaymentMethodsActionError())}
        />
      )}

      {/* #genai Non-409 remove failures — same Banner chrome as blocked (desktop + mobile). HTTP 500
          always shows Figma body (ignores API errorDescription); other statuses prefer API text. */}
      {actionError?.kind === PAYMENT_METHODS_ACTION_ERROR_KIND.Remove && (
        <Banner
          severity={ERROR}
          title={isRemove500
            ? t('billing_information__pm_remove_failed_500_title', { lastFour: removeFailedLastFour })
            : t('billing_information__pm_remove_failed_title')}
          message={isRemove500
            ? (
              // #genai supportLink mirrors SomethingWentWrongDialog (mailto CUSTOMER_SERVICE_EMAIL).
              <Trans
                i18nKey="billing_information__pm_remove_failed_500_message"
                components={{
                  supportLink: (
                    <a href={`mailto:${CUSTOMER_SERVICE_EMAIL}`}>
                      contact customer support
                    </a>
                  ),
                }}
              />
            )
            : (
              actionError.errorDescription
              || t('billing_information__pm_remove_failed_message')
            )}
          handleAlertClose={() => dispatch(resetPaymentMethodsActionError())}
        />
      )}

      <EVGrid className={classes.card}>
        <EVGrid className={classes.cardHeaderRow}>
          <EVTypography className={classes.cardTitle}>
            {t('billing_information__payment_methods_title')}
          </EVTypography>
          <AddPaymentMethodButton id="saved-pm-add-payment-method-btn" />
        </EVGrid>

        <EVGrid className={classes.list}>
          {methods.map((method) => (
            <SavedPaymentMethodRow
              key={paymentMethodKey(method.apiType, method.id)}
              method={method}
              isPending={pendingActionKey === paymentMethodKey(method.apiType, method.id)}
              collapseNonce={collapseNonce}
              onRemove={handleRemoveRequest}
              onMakeDefault={handleMakeDefault}
            />
          ))}
        </EVGrid>
      </EVGrid>

      <RemovePaymentMethodDialog
        open={Boolean(removeTarget)}
        methodLabel={getMethodLabel(removeTarget)}
        lastFour={removeTarget ? removeTarget.lastFour : ''}
        onCancel={handleRemoveCancel}
        onConfirm={handleRemoveConfirm}
      />
    </EVGrid>
  );
}

export default SavedPaymentMethods;
